import PassengerType from "@CheckOrder/types/Passenger.type";
import {isPassengerVehicle} from "@CheckOrder/functions/isPassengerVehicle";
import VehicleAddonType from "@CheckOrder/types/VehicleAddon.type";
import manageClassToPassengersMapping from "@CheckOrder/reducers/functions/manageClassToPassengersMapping";
import SelectedPassengerClassesType
    from "@CheckOrder/features/classPicker/types/selectedPassengerClass/SelectedPassengerClasses.type";
import AvailablePassengerClassType from "@CheckOrder/features/classPicker/types/AvailablePassengerClass.type";

/**
 * The hardest part of the passenger class implementation
 * Logic for auto assigning passenger classes to more than one user (current user in the check order form)
 *
 * Current logic:
 *  For every next user in the check order form (users beneath the currently filled in user on page)
 *    - check whether he has a passenger class assigned
 *    - check whether he can be added to the selected passenger class
 */
export default function getPassengerToAutoAssignSelectedPassengerClass(
    selectedPassengerClasses: SelectedPassengerClassesType,
    passengers: (PassengerType | VehicleAddonType)[],
    passengerId: number,
    passengerClass: AvailablePassengerClassType,
    passengerGroupIndex?: number
) {
    const {
        findPassengerInClassToPassengersMapping,
    } = manageClassToPassengersMapping()

    /**
     * Returns only assignable/currently unassigned passengerIds
     */
    const filterOutAssignedPassengers = (nextPassengersInCheckOrderForm: number[]) => (
        nextPassengersInCheckOrderForm.filter((passengerId) => {
            if (selectedPassengerClasses.passengerToPassengerClassMap[passengerId]?.isDefault) {
                return true
            }

            const {passengersClassToPassengerMapping} = findPassengerInClassToPassengersMapping(selectedPassengerClasses.classToPassengersMap, passengerId)
            return !passengersClassToPassengerMapping
        })
    )

    /**
     * Gets next passengers in the check order form
     */
    const getNextPassengersInCheckOrderList = () => {
        const onlyPassengers: PassengerType[] = passengers.filter((passenger) => (!isPassengerVehicle(passenger)))

        const passengerIndex = onlyPassengers.findIndex((passenger) => (
            passenger.passengerId === passengerId
        ))

        const nextPassengersInCheckOrderForm = onlyPassengers.slice(passengerIndex).map((passenger) => (
            passenger.passengerId
        ))

        return nextPassengersInCheckOrderForm
    }

    // if passenger class accepts only 1 user OR
    // passengerGroupIndex is defined (it is already defined to which user group this single user is going to be added)
    // then this logic is unnecessary
    if (passengerClass.users === 1 || passengerGroupIndex !== undefined) {
        return [passengerId]
    }

    const nextPassengersInCheckOrderForm = getNextPassengersInCheckOrderList()
    let unassignedPassengers = filterOutAssignedPassengers(nextPassengersInCheckOrderForm)

    if (unassignedPassengers.length) {
        if (unassignedPassengers.length > passengerClass.users) {
            unassignedPassengers = unassignedPassengers.slice(0, passengerClass.users)
        }

        return unassignedPassengers
    }

    return [
        passengerId,
    ]
}